import React, { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { InfoIcon, PlusIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { useHydration } from '../contexts/HydrationContext';
import { useLocale } from '../contexts/LocaleContext';
import { HYDRATION_ML_PER_KG, HYDRATION_QUICK_ADD_ML, formatLiters } from '../utils/hydration';
interface HydrationMeterProps {
  /** compact = Home summary line · full = Daily Check with quick-add. */
  variant?: 'compact' | 'full';
}

/**
 * MOB/Hydration/Meter — reads the personalized target from the hydration
 * rule. No screen computes a hydration value on its own.
 */
export function HydrationMeter({
  variant = 'compact'
}: HydrationMeterProps) {
  const {
    t,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const hydration = useHydration();
  const [infoOpen, setInfoOpen] = useState(false);
  const titleId = useId();
  if (hydration.weightMissing || !hydration.targetMl) {
    return <div className="rounded-card border border-gold/35 bg-sand-deep/50 p-2" data-id="element-799" data-mp="20etvv9vl5912">
        <p className="text-[12.5px] leading-snug text-deep" data-id="element-800" data-mp="1vhagu7r2cs82">{t('hydration.noWeight')}</p>
        <Button variant="outline" className="mt-2" onClick={() => navigate('/perfil/corpo/editar')} data-id="element-801" data-mp="2a5evn0mzwjlk">
          {t('hydration.addWeight')}
        </Button>
      </div>;
  }
  const intake = formatLiters(hydration.intakeMl, locale);
  const target = formatLiters(hydration.targetMl, locale);
  const pct = Math.round(hydration.ratio * 100);
  return <div data-id="element-802" data-mp="1qjsb0q8cet4a">
      <div className="flex items-center justify-between gap-2" data-id="element-803" data-mp="1qkad43hos1k4">
        <div className="min-w-0" data-id="element-804" data-mp="1mgm4ril8d1xt">
          <p className="bee-overline text-[9px] font-bold text-muted" data-id="element-805" data-mp="1mfki0guuqr09">
            {t('hydration.target')}
          </p>
          <p className="bee-num mt-[2px] text-[13.5px] font-bold text-deep" data-id="element-806" data-mp="31zyywrj7eujc">
            {t('hydration.progress', {
            intake,
            target
          })}
          </p>
        </div>
        <button type="button" onClick={() => setInfoOpen(true)} aria-label={t('hydration.info.open')} className="grid h-11 w-11 shrink-0 place-items-center rounded-pill text-muted transition-colors duration-150 ease-brand hover:bg-sand-deep hover:text-deep" data-id="element-807" data-mp="1ukbuvce13u6w">
          <InfoIcon size={17} data-id="element-808" data-mp="28juwdmcc47a8" />
        </button>
      </div>

      <div className="mt-1 h-[5px] overflow-hidden rounded-pill bg-sand-deep" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={t('hydration.target')} data-id="element-809" data-mp="1bv5e3uwspcnq">
        <motion.div className="h-full rounded-pill bg-deep" initial={false} animate={{
        width: `${pct}%`
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-810" data-mp="1ku0qjoqfnizh" />
      </div>

      {variant === 'full' && <div className="mt-2 flex flex-wrap gap-1" data-id="element-811" data-mp="x5hinabap87x">
          {HYDRATION_QUICK_ADD_ML.map(ml => <button key={ml} type="button" onClick={() => hydration.addWater(ml)} className="flex min-h-[44px] items-center gap-1 rounded-pill border border-sand-line bg-white/70 px-3 text-[12.5px] font-medium text-deep transition-colors duration-150 ease-brand hover:border-deep/30" data-id="element-812" data-mp="pmqa6r0ecbx4">
              <PlusIcon size={14} aria-hidden="true" data-id="element-813" data-mp="botf5z1lcdkk" />
              <span className="bee-num" data-id="element-814" data-mp="6onqmrqmb76p">{ml} ml</span>
            </button>)}
        </div>}

      <Modal open={infoOpen} onClose={() => setInfoOpen(false)} labelledBy={titleId} data-id="element-815" data-mp="1w43b324oxop">
        <h2 id={titleId} className="pr-5 text-title text-deep" data-id="element-816" data-mp="3t48i0yg65ixj">
          {t('hydration.info.title')}
        </h2>
        <p className="mt-2 text-[13px] leading-relaxed text-deep" data-id="element-817" data-mp="3p2wmkcam63hf">
          {t('hydration.info.body', {
          ml: HYDRATION_ML_PER_KG
        })}
        </p>
        {hydration.weightKg != null && <p className="bee-num mt-2 rounded-card bg-sand-deep/60 px-2 py-2 text-[12.5px] font-medium text-deep" data-id="element-818" data-mp="3f4gyki6vvac5">
            {t('hydration.info.formula', {
          kg: hydration.weightKg.toLocaleString(locale, {
            maximumFractionDigits: 1
          }),
          ml: HYDRATION_ML_PER_KG,
          target
        })}
          </p>}
        <p className="mt-2 text-[11.5px] leading-snug text-muted" data-id="element-819" data-mp="198n7r61hz6n5">
          {t('hydration.disclaimer')}
        </p>
        <Button variant="primary" fullWidth className="mt-3" onClick={() => setInfoOpen(false)} data-id="element-820" data-mp="10a1iyt5fmvdx">
          {t('common.close')}
        </Button>
      </Modal>
    </div>;
}